/**
 * LocationPicker — 지역 선택 모달
 * 좌측: 시도, 우측: 시군구/동, 하단: 선택 칩 + 적용
 * localStorage 'topgrade.location'에 선택 동네 배열 저장
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';

// 1차 더미 데이터 — 추후 공공데이터(data.go.kr 표준 행정구역)로 교체
const REGIONS = {
  서울: ['강남구 역삼동', '강남구 대치동', '강남구 청담동', '강남구 삼성동', '서초구 방배동', '서초구 잠원동', '송파구 잠실동'],
  경기: ['고양시 일산동구', '고양시 일산서구', '고양시 덕양구', '고양시 백석동', '고양시 마두동', '고양시 문봉동', '성남시 분당구 정자동', '성남시 분당구 서현동'],
  인천: ['연수구 송도동', '서구 청라동', '남동구 구월동'],
  강원: ['춘천시 효자동', '강릉시 교동'],
  경남: ['창원시 의창구', '진주시 가호동'],
  경북: ['포항시 남구', '경주시 황남동'],
};
const SIDOS = Object.keys(REGIONS);

const LOC_KEY = 'topgrade.location';

export const loadLocation = () => {
  try {
    const raw = localStorage.getItem(LOC_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
};
export const saveLocation = (list) => {
  try { localStorage.setItem(LOC_KEY, JSON.stringify(list)); } catch {}
};

export const labelOf = (list) => {
  if (!list || list.length === 0) return '지역 선택';
  const first = list[0].split(' ').slice(-1)[0]; // 마지막 단어(동/구)
  return list.length === 1 ? first : `${first} 외 ${list.length - 1}`;
};

const LocationPicker = ({ initial = [], onClose, onApply }) => {
  const [sido, setSido] = useState(SIDOS[1]); // 경기 디폴트
  const [selected, setSelected] = useState(initial);

  const toggle = (name) => {
    setSelected((cur) =>
      cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]
    );
  };

  const apply = () => {
    saveLocation(selected);
    onApply?.(selected);
    onClose?.();
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Head>
          <Title>위치 기준</Title>
          <Close type="button" onClick={onClose}><IoClose size={22} /></Close>
        </Head>

        <Body>
          <SidoCol>
            {SIDOS.map((s) => (
              <SidoBtn
                key={s}
                type="button"
                $on={sido === s}
                onClick={() => setSido(s)}
              >
                {s}
              </SidoBtn>
            ))}
          </SidoCol>
          <GuList>
            {REGIONS[sido].map((g) => {
              const on = selected.includes(g);
              return (
                <GuBtn key={g} type="button" $on={on} onClick={() => toggle(g)}>
                  {g}
                </GuBtn>
              );
            })}
          </GuList>
        </Body>

        {selected.length > 0 && (
          <Chips>
            {selected.map((s) => (
              <Chip key={s} type="button" onClick={() => toggle(s)}>
                {s.split(' ').slice(-1)[0]} <IoClose size={13} />
              </Chip>
            ))}
          </Chips>
        )}

        <ApplyBtn type="button" onClick={apply}>적용</ApplyBtn>
      </Sheet>
    </Overlay>
  );
};

export default LocationPicker;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 380px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Title = styled.p`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Close = styled.button`
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const SidoCol = styled.div`
  width: 80px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #F7F8FA;
  overflow-y: auto;
`;

const SidoBtn = styled.button`
  padding: 13px 0;
  font-size: 13.5px;
  font-weight: 700;
  background: ${({ $on }) => ($on ? '#fff' : 'transparent')};
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.secondary)};
`;

const GuList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const GuBtn = styled.button`
  width: 100%;
  text-align: left;
  padding: 13px 16px;
  font-size: 13.5px;
  font-weight: 600;
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#fff')};
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.primary)};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 16px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12.5px;
  font-weight: 700;
`;

const ApplyBtn = styled.button`
  margin: 14px 16px 16px;
  padding: 14px;
  border-radius: 13px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 15px;
  font-weight: 800;
`;
